<template>
  <div class="min-h-screen bg-white">
    <div class="container mx-auto px-4 py-8">
      <div class="max-w-[1408px] mx-auto">
        <!-- Breadcrumb -->
        <div class="mb-4 md:mb-8">
          <nav class="flex items-center gap-1 md:gap-2 font-archivo text-xs md:text-sm text-black/70">
            <router-link to="/" class="hover:text-black">{{ $t('breadcrumb.home') }}</router-link>
            <span>/</span>
            <router-link to="/my-account" class="hover:text-black">
              {{ $t('myAccount.title') }}
            </router-link>
            <span>/</span>
            <span class="text-black">{{ $t('orders.title') }}</span>
          </nav>
        </div>

        <!-- Header -->
        <div class="mb-4 md:mb-8">
          <h1 class="font-archivo-narrow font-semibold text-2xl md:text-[34px] leading-8 md:leading-[40px]">
            {{ $t('orders.title') }}
          </h1>
        </div>

        <!-- Filter by order number (parity with order management) -->
        <div class="mb-4 md:mb-6 p-3 md:p-4 bg-[#FAFAFA] rounded-lg border border-black/5">
          <label class="block text-xs md:text-sm font-medium font-archivo mb-1.5">{{ $t('ordersSettings.orderNumberSearch') }}</label>
          <div class="flex flex-col sm:flex-row gap-2 sm:items-center max-w-xl">
            <input
              v-model.trim="orderNumberInput"
              type="text"
              :placeholder="$t('ordersSettings.orderNumberPlaceholder')"
              class="flex-1 border border-gray-300 rounded px-3 py-2 text-sm font-archivo focus:outline-none focus:ring-2 focus:ring-empire-yellow"
              @keyup.enter="applyOrderNumberFilter"
            />
            <div class="flex gap-2 shrink-0">
              <button
                type="button"
                class="bg-empire-yellow hover:bg-yellow-400 text-black font-archivo font-semibold py-2 px-4 rounded text-sm"
                @click="applyOrderNumberFilter"
              >
                {{ $t('ordersSettings.applyFilters') }}
              </button>
              <button
                v-if="appliedOrderNumberFilter"
                type="button"
                class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-archivo py-2 px-4 rounded text-sm"
                @click="clearOrderNumberFilter"
              >
                {{ $t('orders.clearOrderFilter') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4 md:py-8">
          <div class="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-empire-yellow mx-auto"></div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-4 md:py-8 text-red-600 text-sm md:text-base">
          {{ error }}
        </div>

        <!-- Empty State (no filter) -->
        <div v-else-if="!orders.length && !appliedOrderNumberFilter" class="text-center py-4 md:py-8">
          <svg class="w-12 h-12 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 17h6M9 13h6M3 8h18v13H3V8zM3 8l2-4h14l2 4"/>
          </svg>
          <p class="text-lg md:text-xl font-archivo mb-3 md:mb-4">{{ $t('orders.noOrders') }}</p>
          <router-link
            to="/categories"
            class="inline-block bg-empire-yellow text-black font-archivo font-semibold py-2 md:py-3 px-4 md:px-6 text-sm md:text-base rounded hover:bg-yellow-400 transition-colors"
          >
            {{ $t('orders.startShopping') }}
          </router-link>
        </div>

        <!-- No results with the order number filter -->
        <div v-else-if="!orders.length && appliedOrderNumberFilter" class="text-center py-4 md:py-8">
          <p class="text-lg md:text-xl font-archivo mb-3 md:mb-4 text-black/80">{{ $t('orders.noOrdersForFilter') }}</p>
          <button
            type="button"
            class="inline-block bg-empire-yellow text-black font-archivo font-semibold py-2 md:py-3 px-4 md:px-6 text-sm md:text-base rounded hover:bg-yellow-400 transition-colors"
            @click="clearOrderNumberFilter"
          >
            {{ $t('orders.clearOrderFilter') }}
          </button>
        </div>

        <!-- Orders List -->
        <div v-else class="space-y-4 md:space-y-6">
          <div v-for="order in orders" :key="order.id" class="border-2 border-black/10 rounded-lg p-3 md:p-6">
            <div
              class="flex flex-wrap justify-between items-start gap-2 md:gap-4 mb-2 md:mb-4 cursor-pointer"
              @click="toggleOrder(order.id)"
            >
              <div>
                <h3 class="font-archivo-narrow font-semibold text-base md:text-xl mb-0.5 md:mb-1">
                  {{ $t('orders.orderNumber', { number: order.order_number }) }}
                </h3>
                <p class="text-black/60 font-archivo text-xs md:text-sm">
                  {{ formatDate(order.created_at) }}
                </p>
              </div>
              <div class="text-right">
                <p v-if="!isOrderExpanded(order.id) && !isOrderWithoutPrices(order)" class="font-archivo-narrow font-semibold text-base md:text-xl">
                  {{ formatPrice(calculateOrderTotal(order)) }}
                </p>
                <p v-if="!isOrderExpanded(order.id) && isOrderWithoutPrices(order)" class="font-archivo-narrow font-semibold text-base md:text-xl text-gray-500">
                  {{ $t('orders.noPriceOrder') }}
                </p>
                <span :class="getStatusClass(order.status.toLowerCase())" class="inline-block px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm">
                  {{ $t(`orders.status.${order.status.toLowerCase()}`) }}
                </span>
              </div>
            </div>

            <!-- Order Items -->
            <div v-if="isOrderExpanded(order.id)">
              <div class="flex justify-end mt-2 md:mt-3">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 font-archivo text-sm font-semibold text-gray-800 hover:text-black border-2 border-black/15 hover:border-black/30 rounded-lg px-3 py-2 transition-colors disabled:opacity-50"
                  :disabled="loadingPdfForId === order.id"
                  :title="$t('ordersSettings.printOrderTitle')"
                  @click.stop="openOrderPdfViewer(order)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  {{ $t('ordersSettings.printOrder') }}
                </button>
              </div>
              <div class="mt-3 md:mt-4 border-t border-black/10 pt-3 md:pt-4">
                <div v-for="item in order.items" :key="item.product_id" class="flex items-center gap-2 md:gap-4 w-full mb-3 md:mb-4 last:mb-0">
                  <!-- Main product container -->
                  <div class="flex items-center gap-1 md:gap-2 flex-1 min-w-0 h-16">
                    <!-- Product image -->
                    <div class="shrink-0">
                      <router-link
                        v-if="item.product_id"
                        :to="{ name: 'ProductDetails', params: { id: item.product_id } }"
                        class="block"
                      >
                        <img
                          :src="imageService.getProductImageUrl(item.image)"
                          :alt="item.product_name"
                          @error="handleImageError"
                          class="w-12 h-12 md:w-16 md:h-16 object-cover rounded"
                        />
                      </router-link>
                      <img
                        v-else
                        :src="imageService.getProductImageUrl(item.image)"
                        :alt="item.product_name"
                        @error="handleImageError"
                        class="w-12 h-12 md:w-16 md:h-16 object-cover rounded shrink-0"
                      />
                    </div>

                    <!-- Name and quantity container -->
                    <div class="flex flex-col sm:flex-row items-center gap-2 min-w-0 flex-1">
                      <!-- Description, product name and quantity (on the same line on mobile) -->
                      <div class="min-w-0 sm:w-[250px] md:w-[525px] flex flex-col justify-center my-auto">  <!-- Fixed height removed and my-auto added for perfect vertical centering -->
                        <!-- Product description in bold (swapped with the name) -->
                        <div class="overflow-hidden mb-0">
                          <router-link
                            v-if="item.product_id"
                            :to="{ name: 'ProductDetails', params: { id: item.product_id } }"
                            class="hover:text-empire-yellow transition-colors"
                          >
                            <p class="font-archivo font-bold text-xs md:text-sm leading-tight text-black/90">
                              {{ item.product_description || item.product_name }}
                            </p>
                          </router-link>
                          <p v-else class="font-archivo font-bold text-xs md:text-sm leading-tight text-black/90">
                            {{ item.product_description || item.product_name }}
                          </p>
                        </div>

                        <div class="flex items-center justify-between">
                          <!-- Product name -->
                          <div class="min-w-0 flex-1 pr-2">
                            <router-link
                              v-if="item.product_id"
                              :to="{ name: 'ProductDetails', params: { id: item.product_id } }"
                              class="hover:text-empire-yellow transition-colors font-archivo-narrow font-light text-[10px] md:text-[14px] leading-tight text-black/70 block truncate product-name"
                            >
                              {{ item.product_name }}
                            </router-link>
                            <span v-else class="font-archivo-narrow font-light text-[10px] md:text-[14px] leading-tight text-black/70 block truncate product-name">{{ item.product_name }}</span>
                          </div>

                          <!-- Quantity on mobile (visible on mobile only) -->
                          <div class="sm:hidden flex items-center gap-1 font-archivo text-[10px] whitespace-nowrap">
                            <div class="flex items-center justify-center bg-black/5 px-1.5 py-0.5 rounded">
                              <!-- When there is a price, show quantity, x, price -->
                              <template v-if="!isOrderWithoutPrices(order)">
                                <span class="font-semibold">{{ item.quantity }}</span>
                                <span class="mx-1 text-black/60">x</span>
                                <span>{{ formatPrice(item.unit_price) }}</span>
                              </template>
                              <!-- When there is no price, show quantity, x -->
                              <template v-else>
                                <span class="font-semibold">{{ item.quantity }}</span>
                                <span class="mx-1 text-black/60">x</span>
                              </template>
                            </div>
                          </div>
                        </div>

                        <!-- Product characteristics -->
                        <div v-if="item.color || item.size || item.weight || item.unit" class="flex flex-wrap gap-x-2 md:gap-x-3 mt-0.5 md:mt-1">
                          <span v-if="item.color" class="text-[10px] md:text-xs text-gray-600 flex items-center gap-0.5 md:gap-1">
                            <span class="font-semibold">{{ $t('productDetails.selectColor') }}:</span>
                            <span class="flex items-center gap-1">
                              <ColorCircle :color="item.color" :size="12" />
                            </span>
                          </span>
                          <span v-if="item.size" class="text-[10px] md:text-xs text-gray-600">
                            <span class="font-semibold">{{ $t('productDetails.selectSize') }}:</span> {{ item.size }}
                          </span>
                          <span v-if="item.weight" class="text-[10px] md:text-xs text-gray-600">
                            <span class="font-semibold">{{ $t('productDetails.selectWeight') }}:</span> {{ item.weight }}
                          </span>
                          <span v-if="item.unit" class="text-[10px] md:text-xs text-gray-600">
                            <span class="font-semibold">{{ $t('productDetails.selectUnit') }}:</span> {{ item.unit }}
                          </span>
                        </div>
                      </div>

                      <!-- Quantity and unit price (visible on desktop only) -->
                      <div class="hidden sm:flex items-center gap-1 font-archivo text-xs whitespace-nowrap mx-auto pt-2 md:pt-4">
                        <div class="flex items-center justify-center bg-black/5 px-2 md:px-3 py-0.5 md:py-1 rounded">
                          <!-- When there is a price, show quantity, x, price -->
                          <template v-if="!isOrderWithoutPrices(order)">
                            <span class="font-semibold">{{ item.quantity }}</span>
                            <span class="mx-2 text-black/60">x</span>
                            <span class="text-sm">{{ formatPrice(item.unit_price) }}</span>
                          </template>
                          <!-- When there is no price, show quantity, x -->
                          <template v-else>
                            <span class="font-semibold">{{ item.quantity }}</span>
                            <span class="mx-2 text-black/60">x</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Total price with a fixed width (shown only if the order has prices) -->
                  <div v-if="!isOrderWithoutPrices(order)" class="w-[60px] sm:w-[80px] md:w-[120px] flex-shrink-0 text-right sm:self-center">
                    <p class="font-archivo-narrow font-semibold text-xs sm:text-sm md:text-xl">
                      {{ formatPrice(item.total_price) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Order summary -->
              <div class="border-t border-black/10 mt-4 md:mt-6 pt-3 md:pt-4">
                <div class="flex flex-wrap justify-between">
                  <!-- Order information: Address and Additional Information -->
                  <div class="w-full md:w-3/4 mb-4 md:mb-0 pr-0 md:pr-8">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <!-- Notes Section (oculta) -->
                      <div v-if="false">
                        <h4 class="font-archivo-narrow font-semibold text-sm md:text-base mb-1 md:mb-2">{{ $t('orders.notes') }}</h4>
                        <div class="bg-black/5 rounded-lg p-2 md:p-3 max-h-[80px] md:max-h-[100px] overflow-y-auto">
                          <p v-if="order.notes" class="font-archivo text-xs whitespace-pre-wrap notes-text">
                            {{ order.notes }}
                          </p>
                          <p v-else class="font-archivo text-xs text-black/50 italic">
                            {{ $t('orders.noNotes') }}
                          </p>
                        </div>
                      </div>

                      <!-- Shipping/Pickup Address Section (first column) -->
                      <div>
                        <h4 class="font-archivo-narrow font-semibold text-sm md:text-base mb-1 md:mb-2">
                          {{ order.delivery_method === 'pickup' ? $t('orders.pickupLocation') : $t('orders.shippingAddress') }}
                        </h4>
                        <div class="bg-black/5 rounded-lg p-2 md:p-3 max-h-[150px] md:max-h-[180px] overflow-y-auto">
                          <!-- PO Number (shown for both delivery methods) -->
                          <div v-if="order.po_number" class="flex items-start gap-2 mb-2">
                            <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mt-0.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                            <div>
                              <div><span class="font-medium font-archivo text-xs">{{ $t('orders.poNumber') }}: </span></div>
                              <div class="font-semibold font-archivo text-xs">{{ order.po_number }}</div>
                            </div>
                          </div>

                          <!-- Shipping address (delivery) -->
                          <div v-if="order.delivery_method === 'delivery' && order.address" class="flex items-start gap-2">
                            <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                            <div class="font-archivo text-xs">
                              <span class="font-medium">{{ order.customer_name }}</span>
                              <span class="block">{{ order.address }}{{ order.landmark ? ', ' + order.landmark : '' }}</span>
                              <span class="block">{{ order.city }}{{ order.state ? ', ' + order.state : '' }} {{ order.postal_code }}</span>
                              <span v-if="order.country" class="block">{{ order.country }}</span>
                              <span class="block mt-1 flex items-center" v-if="order.phone">
                                <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                {{ order.phone }}
                              </span>
                              <!-- Special Delivery Instructions (without a label or icon) -->
                              <span class="block mt-1 text-gray-600 italic" v-if="order.special_instructions">
                                {{ order.special_instructions }}
                              </span>
                            </div>
                          </div>

                          <!-- Local de retirada (pickup) -->
                          <div v-else-if="order.delivery_method === 'pickup'" class="flex items-start gap-2">
                            <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                            </svg>
                            <div class="font-archivo text-xs">
                              <span class="font-medium">{{ $t('orders.pickupAt') }}</span>
                              <span class="block">{{ order.location_description || $t('orders.noLocationSpecified') }}</span>
                              <!-- Special Pickup Instructions (without a label or icon) -->
                              <span class="block mt-1 text-gray-600 italic" v-if="order.special_instructions">
                                {{ order.special_instructions }}
                              </span>
                            </div>
                          </div>

                          <p v-else class="font-archivo text-xs text-black/50 italic">
                            {{ $t('orders.noShippingAddress') }}
                          </p>
                        </div>
                      </div>

                      <!-- Additional Info Section (second column) -->
                      <div>
                        <h4 class="font-archivo-narrow font-semibold text-sm md:text-base mb-1 md:mb-2">{{ $t('orders.additionalInfo') }}</h4>
                        <div class="bg-black/5 rounded-lg p-2 md:p-3 max-h-[150px] md:max-h-[180px] overflow-y-auto">
                          <div class="flex items-start gap-2">
                            <div class="font-archivo text-xs">
                              <!-- Project Name (if available) -->
                              <div class="flex items-center">
                                <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                                </svg>
                                <span v-if="order.project_name">
                                  <span class="font-medium">{{ $t('orders.project') }}:</span> {{ order.project_name }}
                                </span>
                                <span v-else>
                                  <span class="font-medium">{{ $t('orders.project') }}:</span> <span class="italic text-black/50">{{ $t('orders.noProject') }}</span>
                                </span>
                              </div>

                              <!-- Branch Name (if available) -->
                              <div class="flex items-center mt-1">
                                <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                                <span v-if="order.branch_description">
                                  <span class="font-medium">{{ $t('orders.branch') }}:</span> {{ order.branch_description }}
                                </span>
                                <span v-else>
                                  <span class="font-medium">{{ $t('orders.branch') }}:</span> <span class="italic text-black/50">{{ $t('orders.noBranch') }}</span>
                                </span>
                              </div>

                              <!-- Contact on Site -->
                              <div v-if="order.contact_on_site" class="mt-2 flex items-start">
                                <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mt-0.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                </svg>
                                <div>
                                  <div><span class="font-medium">{{ $t('orders.contactOnSite') }}: </span></div>
                                  <div class="font-semibold">{{ order.contact_on_site }}</div>
                                </div>
                              </div>

                              <!-- Contact Phone -->
                              <div v-if="order.contact_phone" class="mt-2 flex items-start">
                                <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mt-0.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                <div>
                                  <div><span class="font-medium">{{ $t('orders.contactPhone') }}: </span></div>
                                  <div class="font-semibold">{{ order.contact_phone }}</div>
                                </div>
                              </div>

                              <!-- Delivery Date/Time -->
                              <div v-if="order.delivery_date_time" class="mt-2 flex items-start">
                                <svg class="w-3 h-3 text-empire-yellow flex-shrink-0 mt-0.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <div>
                                  <div><span class="font-medium">{{ $t('orders.deliveryDateTime') }}: </span></div>
                                  <div class="font-semibold">{{ formatDateTime(order.delivery_date_time) }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Totals Section (shown only if the order has prices) -->
                  <div v-if="!isOrderWithoutPrices(order)" class="w-full md:w-1/4">
                    <div class="flex flex-col gap-2 items-end">
                      <div class="flex justify-end gap-4 md:gap-8 w-full">
                        <span class="font-archivo text-black/70 text-xs md:text-sm">{{ $t('orders.shipping') }}</span>
                        <span class="font-archivo w-[80px] md:w-[120px] text-right text-xs md:text-sm">
                          {{ formatPrice(order.shipping_cost) }}
                        </span>
                      </div>

                      <div class="flex justify-end gap-4 md:gap-8 w-full">
                        <span class="font-archivo text-black/70 text-xs md:text-sm">{{ $t('orders.taxes') }}</span>
                        <span class="font-archivo w-[80px] md:w-[120px] text-right text-xs md:text-sm">
                          {{ formatPrice(order.tax_amount) }}
                        </span>
                      </div>

                      <div class="flex justify-end gap-4 md:gap-8 w-full pt-2 border-t border-black/10">
                        <span class="font-archivo-narrow font-semibold text-base md:text-xl">{{ $t('orders.total') }}</span>
                        <span class="font-archivo-narrow font-semibold text-base md:text-xl w-[80px] md:w-[120px] text-right">
                          {{ formatPrice(calculateOrderTotal(order)) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Message for orders without prices -->
                  <div v-if="isOrderWithoutPrices(order)" class="w-full md:w-1/4">
                    <div class="flex flex-col gap-2 items-end">
                      <div class="flex justify-end gap-4 md:gap-8 w-full pt-2">
                        <span class="font-archivo-narrow font-semibold text-base md:text-xl text-gray-500">{{ $t('orders.noPriceOrder') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="flex flex-col items-center mt-8 md:mt-12 mb-12 md:mb-24 w-full gap-3 md:gap-4">
          <div class="flex justify-center items-center gap-2 md:gap-4 w-full">
            <!-- Previous Button -->
            <button
              class="flex items-center justify-center h-8 md:h-10 px-2 md:px-4 gap-1 bg-[#F9F9FB] rounded-lg min-w-[70px] md:min-w-[120px]"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <svg class="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="font-inter font-medium text-xs md:text-base">{{ $t('categoryPage.previous') }}</span>
            </button>

            <!-- Page Numbers -->
            <div class="flex gap-1 md:gap-2">
              <button
                v-for="page in displayedPages"
                :key="page"
                class="w-7 h-7 md:w-10 md:h-10 flex items-center justify-center rounded-lg text-xs md:text-base"
                :class="page === currentPage ? 'bg-black text-white' : 'bg-[#F9F9FB] text-black'"
                @click="changePage(page)"
              >
                {{ page }}
              </button>
            </div>

            <!-- Next Button -->
            <button
              class="flex items-center justify-center h-8 md:h-10 px-2 md:px-4 gap-1 bg-[#F9F9FB] rounded-lg min-w-[70px] md:min-w-[120px]"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              <span class="font-inter font-medium text-xs md:text-base">{{ $t('categoryPage.next') }}</span>
              <svg class="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <!-- Item counter in the pagination -->
          <div class="flex justify-center">
            <span class="font-inter text-xs md:text-base">
              {{ totalItems === 0
                ? $t('categoryPage.noResults')
                : $t('categoryPage.itemsCount', {
                    start: itemRange.start,
                    end: itemRange.end,
                    total: totalItems
                  })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- PDF viewer (same as the order manager) -->
    <div
      v-if="showPdfViewer"
      class="fixed inset-0 z-[60] flex flex-col bg-black/70"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 md:px-4 bg-gray-900 text-white shrink-0">
        <span class="font-archivo text-sm md:text-base truncate pr-2">
          {{ $t('ordersSettings.pdfViewerTitle') }}
          <template v-if="pdfViewerOrderNumber">#{{ pdfViewerOrderNumber }}</template>
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            @click="downloadPdfFile"
            :disabled="!pdfBlob"
            class="px-2 py-1.5 md:px-3 md:py-2 rounded text-xs md:text-sm font-archivo bg-white/10 hover:bg-white/20 disabled:opacity-40"
          >
            {{ $t('ordersSettings.downloadPdf') }}
          </button>
          <button
            type="button"
            @click="printPdfInViewer"
            :disabled="!pdfObjectUrl"
            class="px-2 py-1.5 md:px-3 md:py-2 rounded text-xs md:text-sm font-archivo bg-white/10 hover:bg-white/20 disabled:opacity-40"
          >
            {{ $t('ordersSettings.printPdf') }}
          </button>
          <button
            type="button"
            @click="closePdfViewer"
            class="px-2 py-1.5 md:px-3 md:py-2 rounded text-xs md:text-sm font-archivo bg-empire-yellow text-black hover:bg-yellow-400"
          >
            {{ $t('ordersSettings.closePdf') }}
          </button>
        </div>
      </div>
      <div class="flex-1 min-h-0 bg-gray-800 relative">
        <div v-if="pdfLoading" class="absolute inset-0 flex items-center justify-center bg-white">
          <div class="flex flex-col items-center gap-3">
            <div class="w-10 h-10 border-4 border-empire-yellow border-t-transparent rounded-full animate-spin" />
            <span class="font-archivo text-sm text-gray-600">{{ $t('ordersSettings.loadingPdf') }}</span>
          </div>
        </div>
        <iframe
          v-show="pdfObjectUrl && !pdfLoading"
          ref="pdfIframeRef"
          :src="pdfObjectUrl"
          title="PDF"
          class="w-full h-full border-0 bg-gray-700"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import api from '@/services/api'
import { getMyOrderPdfBlob } from '@/services/orderService'
import ColorCircle from '@/components/common/ColorCircle.vue'
import { useI18n } from 'vue-i18n'
import { imageService } from '@/services/imageService'
import { productService } from '@/services/productService'
import { getProductUrlWithDescription } from '@/utils/urlUtils'
// Removed the unused import of PLACEHOLDER_IMAGE_PATH and NO_IMAGE_PATH

const orders = ref([])
const loading = ref(false)
const error = ref(null)
const currentPage = ref(1)
const totalPages = ref(1)
const totalItems = ref(0)
const itemsPerPage = 5
const expandedOrders = ref([]) // Added a ref to control the expanded orders
const productDescriptions = ref({}) // Stores the product descriptions
const { locale, t } = useI18n() // Gets the current locale and the translation function

const orderNumberInput = ref('')
const appliedOrderNumberFilter = ref('')

const showPdfViewer = ref(false)
const pdfObjectUrl = ref(null)
const pdfBlob = ref(null)
const pdfLoading = ref(false)
const loadingPdfForId = ref(null)
const pdfViewerOrderNumber = ref('')
const pdfIframeRef = ref(null)

const applyOrderNumberFilter = async () => {
  appliedOrderNumberFilter.value = orderNumberInput.value.trim()
  currentPage.value = 1
  await fetchOrders(1)
}

const clearOrderNumberFilter = async () => {
  orderNumberInput.value = ''
  appliedOrderNumberFilter.value = ''
  currentPage.value = 1
  await fetchOrders(1)
}

const closePdfViewer = () => {
  if (pdfObjectUrl.value) {
    URL.revokeObjectURL(pdfObjectUrl.value)
    pdfObjectUrl.value = null
  }
  pdfBlob.value = null
  showPdfViewer.value = false
  pdfLoading.value = false
  pdfViewerOrderNumber.value = ''
}

const openOrderPdfViewer = async (order) => {
  if (!order?.id) return
  loadingPdfForId.value = order.id
  showPdfViewer.value = true
  pdfLoading.value = true
  pdfViewerOrderNumber.value =
    order.order_number != null && String(order.order_number).trim() !== ''
      ? String(order.order_number)
      : String(order.id)
  if (pdfObjectUrl.value) {
    URL.revokeObjectURL(pdfObjectUrl.value)
    pdfObjectUrl.value = null
  }
  pdfBlob.value = null
  try {
    const loc = String(locale.value || '').toLowerCase() === 'en' ? 'en' : 'fr'
    const blob = await getMyOrderPdfBlob(order.id, loc)
    pdfBlob.value = blob
    pdfObjectUrl.value = URL.createObjectURL(blob)
  } catch (e) {
    console.error('Error loading order PDF:', e)
    alert(t('ordersSettings.pdfLoadError'))
    closePdfViewer()
  } finally {
    pdfLoading.value = false
    loadingPdfForId.value = null
  }
}

const downloadPdfFile = () => {
  if (!pdfBlob.value) return
  const safe = String(pdfViewerOrderNumber.value || 'order').replace(/[^\w.-]+/g, '_')
  const url = URL.createObjectURL(pdfBlob.value)
  const a = document.createElement('a')
  a.href = url
  a.download = `order-${safe}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1500)
}

const printPdfInViewer = () => {
  const el = pdfIframeRef.value
  if (el?.contentWindow) {
    el.contentWindow.focus()
    el.contentWindow.print()
  }
}

// Computed for the pages to display
const displayedPages = computed(() => {
  const pages = []
  const maxVisiblePages = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2))
  let end = Math.min(totalPages.value, start + maxVisiblePages - 1)

  if (end - start + 1 < maxVisiblePages) {
    start = Math.max(1, end - maxVisiblePages + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Computed property to calculate the item range
const itemRange = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage + 1
  const end = Math.min(start + itemsPerPage - 1, totalItems.value)
  return { start, end }
})

const formatPrice = (price, item = null) => {
  // If the item has lpor = true, return "Price on request" in the user language
  if (item && item.lpor === true) {
    return t('products.priceOnRequest')
  }
  
  if (price === null || price === undefined) {
    return t('products.priceOnRequest')
  }
  const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
  // If the price is NaN or negative, show "Price on request"
  // Price 0.00 is valid and must be displayed normally
  if (isNaN(numericPrice) || numericPrice < 0) {
    return t('products.priceOnRequest')
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(numericPrice)
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '';
  const date = new Date(dateTimeString);
  // Show only the date, without the time
  return date.toLocaleDateString();
}

const getStatusClass = (status) => {
  const classes = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800'
  }
  return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
}

const toggleOrder = (orderId) => {
  const index = expandedOrders.value.indexOf(orderId)
  if (index === -1) {
    expandedOrders.value.push(orderId)
  } else {
    expandedOrders.value.splice(index, 1)
  }
}

const isOrderExpanded = (orderId) => {
  return expandedOrders.value.includes(orderId)
}

// Function to load the product descriptions
const loadProductDescriptions = async (items) => {
  try {
    const updatedItems = [...items];
    let hasUpdates = false;

    for (let i = 0; i < updatedItems.length; i++) {
      const item = updatedItems[i];

      // Checks whether we already have the description cached
      if (productDescriptions.value[item.product_id]) {
        console.log('Description already in cache for product', item.product_id);
        // Adds the description directly to the item
        if (!item.product_description || item.product_description === item.product_name) {
          updatedItems[i] = {
            ...item,
            product_description: productDescriptions.value[item.product_id]
          };
          hasUpdates = true;
        }
        continue;
      }

      try {
        console.log('Fetching product details for product', item.product_id);
        const productDetails = await productService.getProductDetails(item.product_id);

        if (productDetails) {
          // Selects the description in the current language or falls back to English
          const description = productDetails[`description_${locale.value}`] ||
                             productDetails.description_en ||
                             productDetails.description_fr ||
                             '';

          console.log('Got description for product', item.product_id, ':', description);

          // Armazena no cache
          productDescriptions.value[item.product_id] = description;

          // Adds the description directly to the item
          updatedItems[i] = {
            ...item,
            product_description: description
          };
          hasUpdates = true;
        }
      } catch (err) {
        console.error('Error fetching product details for product', item.product_id, ':', err);
      }
    }

    return hasUpdates ? updatedItems : items;
  } catch (error) {
    console.error('Error loading product descriptions:', error);
    return items;
  }
};

const fetchOrders = async (page = 1) => {
  loading.value = true
  error.value = null
  try {
    const params = { page, limit: itemsPerPage }
    const num = appliedOrderNumberFilter.value?.trim()
    if (num) params.orderNumber = num

    const response = await api.get(`/orders`, { params })

    // Transforming the data to include the status in uppercase
    const processedOrders = response.data.items.map(order => {
      return {
        ...order,
        status: order.status.toUpperCase()
      };
    });

    // Processes each order to load the product descriptions
    for (let i = 0; i < processedOrders.length; i++) {
      const order = processedOrders[i];
      if (order.items && order.items.length > 0) {
        // Loads the product descriptions for this order
        const updatedItems = await loadProductDescriptions(order.items);
        processedOrders[i].items = updatedItems;
      }
    }

    orders.value = processedOrders;
    currentPage.value = response.data.page
    totalPages.value = response.data.totalPages
    totalItems.value = response.data.total // Add this line to get the total number of items
  } catch (err) {
    console.error('Error fetching orders:', err)
    error.value = 'Error fetching orders'
  } finally {
    loading.value = false
  }
}

const changePage = async (page) => {
  if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
    await fetchOrders(page)
    // Optional: Scroll to the top of the list
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Watches language changes to refresh the descriptions
watch(locale, () => {
  productDescriptions.value = {}
  fetchOrders(currentPage.value)
})

onMounted(() => {
  fetchOrders()
})

onUnmounted(() => {
  if (pdfObjectUrl.value) {
    URL.revokeObjectURL(pdfObjectUrl.value)
  }
})

const handleImageError = (e) => {
  // Uses the imageService utility function to handle image errors
  console.log('[OrdersPage] Tratando erro de imagem')
  imageService.handleImageError(e)
}

// Checks whether the order was placed without prices (master toggle disabled)
const isOrderWithoutPrices = (order) => {
  return order.total === null && order.shipping_cost === null && order.tax_amount === null
}

const calculateOrderTotal = (order) => {
  // If the order was placed without prices, return 0
  if (isOrderWithoutPrices(order)) {
    return 0
  }

  // Ignores items with price -1 in the calculation
  const subtotal = order.items.reduce((total, item) => {
    const itemPrice = Number(item.unit_price)
    if (isNaN(itemPrice) || itemPrice < 0) {
      return total // Ignores items with price -1
    }
    return total + (itemPrice * item.quantity)
  }, 0)
  const shipping = parseFloat(order.shipping_cost) || 0
  const tax = parseFloat(order.tax_amount) || 0

  return subtotal + shipping + tax
}
</script>

<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notes-text {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Style to keep the description height fixed at exactly two lines */
.description-fixed-height {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  height: 2.4em !important; /* Fixed height for exactly 2 lines */
  min-height: 2.4em !important;
  max-height: 2.4em !important;
  line-height: 1.2em !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  word-break: break-word;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Custom style for the product name with extra thin weight */
.product-name {
  font-weight: 100 !important; /* Forces the thinnest possible weight */
  opacity: 0.7 !important; /* Lowers the opacity further so it looks thinner */
  letter-spacing: 0.03em !important; /* Increases the letter spacing further */
  color: rgba(0, 0, 0, 0.6) !important; /* Lighter color so it looks thinner */
  transform: scale(0.98, 1) !important; /* Comprime ligeiramente na horizontal */
}
</style>



